import React from "react";
import SubPageLayout from "../components/SubPageLayout";
import Curriculum from "../components/Curriculum";
import { motion } from "framer-motion";
import { Drama, BookOpen, Hammer, Bus } from "lucide-react";

const EXPERIENTIAL = [
  {
    icon: Drama,
    title: "Role-Playing Scenarios",
    desc: "Enabling children to practice social skills in simulated real-world situations.",
    color: "#648DE5",
  },
  {
    icon: BookOpen,
    title: "Storytelling and Discussions",
    desc: "Helping children reflect on values, emotions, and ethical decision-making.",
    color: "#C6D947",
  },
  {
    icon: Hammer,
    title: "Hands-On Activities",
    desc: "Providing structured experiences that reinforce self-management and responsibility.",
    color: "#5BC4B8",
  },
  {
    icon: Bus,
    title: "Field Trips and Community Engagement",
    desc: "Offering exposure to diverse environments that expand their understanding of the world.",
    color: "#3AB0D6",
  },
];

export default function CurriculumPage() {
  return (
    <SubPageLayout
      eyebrow="Curriculum"
      title={<>Internationally benchmarked. <span className="italic font-normal">Locally rooted</span>.</>}
      subtitle="The Cambridge Early Years framework shapes our days in six gentle pillars — each deliberately woven into play, story and conversation. A foundation that travels with your child for life."
      accent="cornflower"
      decor="circle"
    >
      <div id="cambridge" />
      <div id="pillars" />
      <Curriculum />

      {/* The Experiential Learning Approach (merged) */}
      <section
        id="experiential"
        className="py-14 md:py-24 bg-[#E3EDF9] scroll-mt-32"
        data-testid="curriculum-experiential"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-start mb-14">
            <div className="lg:col-span-5">
              <span className="text-xs font-bold tracking-[0.22em] uppercase text-[#648DE5] mb-4 block">
                The Experiential Learning Approach
              </span>
              <h2 className="font-display text-[32px] md:text-5xl lg:text-6xl text-[#0F1B47] font-semibold leading-tight">
                Learning is <span className="italic font-normal">experiencing</span>.
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-5 text-[#4A5568] text-base md:text-lg leading-relaxed font-light">
              <p>
                The programme is designed to be practical and application-oriented, ensuring
                that children not only learn theoretical concepts but also internalize and
                implement them in their everyday lives.
              </p>
              <p>
                Every climb, every question, every paint-splashed apron is learning. We plan
                around play — not despite it. Teachers are observers first: they document,
                reflect and build each week around what children show them. The result is a
                curriculum that feels alive and responsive, never imposed.
              </p>
              <p className="text-[#0F1B47] font-semibold text-base">
                Our educators integrate four experiential pillars into the everyday rhythm of
                Naavi:
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {EXPERIENTIAL.map((e, i) => {
              const Icon = e.icon;
              return (
                <motion.div
                  key={e.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.55, delay: i * 0.08 }}
                  className="bento-card bg-[#FDFDF9] rounded-3xl p-7 md:p-8 border border-[#0F1B47]/6 flex gap-5 items-start"
                  data-testid={`experiential-${i}`}
                >
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                    style={{ background: `${e.color}1F`, color: e.color }}
                  >
                    <Icon size={20} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-baseline gap-3 mb-2">
                      <span
                        className="font-display text-sm font-semibold"
                        style={{ color: e.color }}
                      >
                        0{i + 1}
                      </span>
                      <h3 className="font-display text-xl text-[#0F1B47] font-medium leading-snug">
                        {e.title}
                      </h3>
                    </div>
                    <p className="text-[#4A5568] text-[15px] leading-relaxed font-light">
                      {e.desc}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </SubPageLayout>
  );
}
